import { authFetch } from './auth-fetch';
import type { Page } from './endpoints';

/**
 * The five user-side calls of `12 §13` — the inbox is personal, so everything
 * here hangs off `/users/me` and filters by the token, never by a Voter.
 *
 * Same rule as every module here: the wire shape verbatim, no client-side
 * modelling. The shapes come from the phase 9 design contract block; the
 * backend is being built against the same block.
 */

export type NotificationSeverity = 'info' | 'warning' | 'error' | 'critical';

export interface NotificationCompany {
  id: string;
  slug: string;
  name: string;
}

export interface Notification {
  id: string;
  /** Null for platform-level notices that belong to no company. */
  company: NotificationCompany | null;
  type: string;
  severity: NotificationSeverity;
  title: string;
  body: string | null;
  /** A route inside this app, e.g. `/c/acme/tasks/128`. */
  action_url: string | null;
  subject_type: string | null;
  subject_id: string | null;
  read_at: string | null;
  created_at: string;
}

/** The listing is a normal page plus `unread_count` in `meta` (design contract). */
export interface NotificationList {
  data: Notification[];
  meta: Page<Notification>['meta'] & { unread_count: number };
}

export interface NotificationPreference {
  /** Null means "all companies" — the least specific half of the rule. */
  company_id: string | null;
  /** `*` for the global rule, otherwise a type like `task.failed`. */
  notification_type: string;
  in_app: boolean;
  email: boolean;
  min_severity: NotificationSeverity;
}

export interface NotificationFilters {
  read?: boolean;
  severity?: NotificationSeverity;
  company_id?: string;
  limit?: number;
}

/**
 * The types a manager most plausibly writes a rule for — the datalist on the
 * preferences table, not a closed catalogue: the input accepts any type.
 */
export const COMMON_NOTIFICATION_TYPES: string[] = [
  '*',
  'approval.requested',
  'task.failed',
  'task.blocked',
  'budget.threshold_reached',
  'budget.exhausted',
  'agreement.proposed',
  'webhook.disabled',
  'pricing.missing',
  'cache.hit_ratio_low',
];

export const notifications = {
  list: (filters: NotificationFilters = {}) => {
    const params = new URLSearchParams();

    if (filters.read !== undefined) params.set('filter[read]', String(filters.read));
    if (filters.severity !== undefined) params.set('filter[severity]', filters.severity);
    if (filters.company_id !== undefined) params.set('filter[company_id]', filters.company_id);
    if (filters.limit !== undefined) params.set('limit', String(filters.limit));

    const query = params.toString();

    return authFetch<NotificationList>(`/users/me/notifications${query === '' ? '' : `?${query}`}`);
  },
  markRead: (id: string) => authFetch<void>(`/notifications/${id}/read`, { method: 'POST' }),
  markAllRead: () => authFetch<void>('/notifications/read-all', { method: 'POST' }),
  preferences: () => authFetch<{ data: NotificationPreference[] }>('/users/me/notification-preferences'),
  /** `PUT` replaces the whole list — the same semantics as `email_recipients`. */
  updatePreferences: (rules: NotificationPreference[]) =>
    authFetch<{ data: NotificationPreference[] }>('/users/me/notification-preferences', {
      method: 'PUT',
      body: { data: rules },
    }),
};

/** Mirror of the PHP publisher's topic: payload `{type: 'notification', notification: {…}}`. */
export function notificationsTopic(companyId: string): string {
  return `/companies/${companyId}/notifications`;
}
